import { useRef, useState, useCallback, useEffect } from "react";
import type { Product } from "@/lib/api";
import ProductCard from "@/components/base/ProductCard";

interface ProductCarouselProps {
  products: Product[];
}

export default function ProductCarousel({ products }: ProductCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, scrollLeft: 0 });

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    // Reset scroll position when products change (e.g. tab switch)
    el.scrollLeft = 0;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, products]);

  const getCardWidth = () => {
    const el = trackRef.current;
    if (!el) return 0;
    const firstCard = el.querySelector<HTMLElement>("[data-carousel-card]");
    return firstCard ? firstCard.offsetWidth + 20 : 0;
  };

  const scrollBy = (dir: "left" | "right") => {
    const el = trackRef.current;
    if (!el) return;
    const step = getCardWidth() * 2;
    el.scrollBy({ left: dir === "left" ? -step : step, behavior: "smooth" });
  };

  const onMouseDown = (e: React.MouseEvent) => {
    const el = trackRef.current;
    if (!el) return;
    setIsDragging(true);
    dragStart.current = { x: e.pageX, scrollLeft: el.scrollLeft };
    el.style.cursor = "grabbing";
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const el = trackRef.current;
    if (!el) return;
    el.scrollLeft = dragStart.current.scrollLeft - (e.pageX - dragStart.current.x);
  };

  const onMouseUp = () => {
    setIsDragging(false);
    if (trackRef.current) trackRef.current.style.cursor = "grab";
  };

  return (
    <div className="relative group/carousel">
      {/* Left Arrow */}
      <button
        onClick={() => scrollBy("left")}
        className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 z-10
          w-10 h-10 flex items-center justify-center bg-moonlight border border-pearl
          text-midnight/60 hover:text-midnight hover:border-midnight
          transition-all duration-200 cursor-pointer whitespace-nowrap
          ${canScrollLeft ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        aria-label="Scroll left"
      >
        <i className="ri-arrow-left-s-line text-lg" />
      </button>

      {/* Right Arrow */}
      <button
        onClick={() => scrollBy("right")}
        className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 z-10
          w-10 h-10 flex items-center justify-center bg-moonlight border border-pearl
          text-midnight/60 hover:text-midnight hover:border-midnight
          transition-all duration-200 cursor-pointer whitespace-nowrap
          ${canScrollRight ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        aria-label="Scroll right"
      >
        <i className="ri-arrow-right-s-line text-lg" />
      </button>

      {/* Track */}
      <div
        ref={trackRef}
        className="flex gap-5 overflow-x-auto scroll-smooth select-none hide-scrollbar"
        style={{ cursor: "grab" }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {products.map((p) => (
          <div
            key={p.id}
            data-carousel-card
            className="flex-none w-[calc(25%-15px)] min-w-[220px]"
          >
            <ProductCard product={p} />
          </div>
        ))}
      </div>
    </div>
  );
}
